import { FC, ReactNode, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import ListBox from "@ingka/list-box";
import Button, { ButtonProps } from "@ingka/button";
import { breakpointL } from "@ingka/variables/design-tokens";
import SSRIcon from "@ingka/ssr-icon";
import chevronDownSmall from "@ingka/ssr-icon/paths/chevron-down-small";
import chevronUpSmall from "@ingka/ssr-icon/paths/chevron-up-small";

import { StyledListView } from "../styles";
import { Counter } from "../Counter/Counter";
import useDropdown from "./useDropown";

export interface ButtonDropdownProps {
	id: string;
	children: ReactNode;
	text: string;
	type?: any;
	ssrIcon: any;
	xsmallIconOnly?: boolean;
	open: boolean;
	setMenuOpen: (value: boolean) => void;
	className: string;
	showCounter?: boolean | null;
	counter?: number;
	size?: ButtonProps["size"];
	ariaLabel?: string;
	noArrow?: boolean;
}

const StyledListBox = styled(ListBox)`
	margin-top: 0.5rem;
	z-index: 1000;
	width: 20rem;

	@media (width < ${breakpointL}) {
		width: 70%;
		right: 2rem;
	}
`;

const StyledCounter = styled(Counter)`
	margin-left: 0.5rem;
`;

const StyledIcon = styled(SSRIcon)`
	margin-left: 0.5rem;
`;

interface CustomButtonProps {
	open?: boolean;
}

const StyledButton = styled(Button)<CustomButtonProps>`
	${({ open }) => {
		return open && "background-color: rgb(223, 223, 223);";
	}}
	.btn__inner {
		padding: 0 1.5rem;
	}
`;

const ButtonDropdown: FC<ButtonDropdownProps> = ({
	id,
	children,
	text,
	ssrIcon,
	xsmallIconOnly,
	type,
	open,
	setMenuOpen,
	className,
	showCounter,
	counter,
	size = "medium",
	ariaLabel,
	noArrow,
}) => {
	const { menuRef, buttonRef, handleToggle, handleClose } = useDropdown({
		open,
		setOpen: setMenuOpen,
	});

	return (
		<>
			<StyledButton
				id={id}
				data-testid={id}
				ref={buttonRef}
				type={type}
				onClick={handleToggle}
				ssrIcon={ssrIcon}
				size={size}
				iconOnly={xsmallIconOnly}
				className={className}
				open={open}
				aria-expanded={open}
				aria-controls={`${id}-listbox`}
				{...(ariaLabel ? { "aria-label": ariaLabel } : {})}
			>
				{text}
				{showCounter && <StyledCounter>{counter}</StyledCounter>}
				{!noArrow && <StyledIcon paths={open ? chevronUpSmall : chevronDownSmall} />}
			</StyledButton>
			<StyledListBox
				id={`${id}-listbox`}
				open={open}
				aria-labelledby={id}
				aria-multiselectable={undefined}
				containerRef={menuRef}
				popupMode
			>
				<StyledListView id={`${id}-list-view`} showDivider size="small">
					{children}
				</StyledListView>
			</StyledListBox>
		</>
	);
};

export default ButtonDropdown;
